import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import uuid from 'uuid/v4';

import Carousel from '@nerdwallet/react-carousel';
import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import { Impression } from '@nerdwallet/analytics/react/impression';
import { SCREEN_MEDIUM } from '@nerdwallet/react-media-queries';
import { Box, Text } from '@nerdwallet/currency';

import {
  boolCheckboxSpec,
  stringInputSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';
import Row from '../row/row';
import Tile from './tile/tile';

import styles from './tile-carousel.module.less';

const TILES = [
  {
    icon: 'creditCards',
    linkUrl: '/l/awards/credit-cards/2020',
    title: 'Credit Cards',
  },
  {
    icon: 'mortgages',
    linkUrl: '/l/awards/mortgages/2020',
    title: 'Mortgages',
  },
  {
    icon: 'banking',
    linkUrl: '/l/awards/banking/2020',
    title: 'Banking',
  },
  {
    icon: 'investing',
    linkUrl: '/l/awards/investing/2020',
    title: 'Investing',
  },
  {
    icon: 'personalLoans',
    linkUrl: '/l/awards/personal-loans/2020',
    title: 'Personal Loans',
  },
  {
    icon: 'studentLoans',
    linkUrl: '/l/awards/student-loans/2020',
    title: 'Student Loans',
  },
  {
    icon: 'autoInsurance',
    linkUrl: '/l/awards/auto-insurance/2020',
    title: 'Auto Insurance',
  },
  {
    icon: 'travelRewards',
    linkUrl: '/l/awards/travel/2020',
    title: 'Travel Rewards',
  },
  {
    icon: 'taxes',
    linkUrl: '/l/awards/taxes/2020',
    title: 'Taxes',
  },
];

const TileCarousel = ({ header, omitTile, sectionCategory, topOverlap }) => {
  const tiles = TILES.filter((tile) => tile.icon !== omitTile);
  const sectionCategoryValue = sectionCategory
    ? `TileCarousel--${sectionCategory}`
    : 'TileCarousel';
  const trkId = uuid();

  return (
    <Row className={classNames(styles.row, topOverlap && styles.topOverlap)}>
      <Impression
        eventType="element_impression"
        entityName="awards_tile_carousel"
      >
        <ElementImpression
          elementContent={header}
          elementName="TileCarousel"
          id={`TileCarousel-${trkId}`}
          sectionCategory={sectionCategory}
        >
          {header && (
            <Box className={styles.tilesTitle}>
              <Text component="h4">{header}</Text>
            </Box>
          )}
          <Carousel
            className={styles.carousel}
            responsive={[
              {
                breakpoint: SCREEN_MEDIUM,
                settings: {
                  arrows: false,
                },
              },
            ]}
            variableWidth
          >
            {tiles.map((tile) => (
              <Tile
                key={tile.icon}
                className={styles.tile}
                {...tile}
                sectionCategory={sectionCategoryValue}
              />
            ))}
          </Carousel>
        </ElementImpression>
      </Impression>
    </Row>
  );
};

TileCarousel.propTypes = {
  header: PropTypes.string,
  omitTile: PropTypes.oneOf(TILES.map((tile) => tile.icon)),
  sectionCategory: PropTypes.string,
  topOverlap: PropTypes.bool,
};

TileCarousel.defaultProps = {
  topOverlap: false,
};

TileCarousel.getEditingConfig = () => [
  stringInputSpec('header', 'Header'),
  stringSelectSpec(
    'sectionCategory',
    'Section (used for tracking)',

    [
      {
        value: null,
        label: 'Select one',
      },
      {
        value: 'header',
        label: 'Header',
      },
      {
        value: 'footer',
        label: 'Footer',
      },
    ]
  ),
  boolCheckboxSpec('topOverlap', 'Top overlap', {
    help: 'The top of tiles overlap with previous builder element.',
  }),
  stringSelectSpec(
    'omitTile',
    'Omit "current" tile',

    [
      {
        value: null,
        label: 'None',
      },
      ...TILES.map((tile) => ({
        value: tile.icon,
        label: tile.title,
      })),
    ]
  ),
];

TileCarousel.displayName = 'TileCarousel';

export default TileCarousel;
